import type { ReactNode } from 'react';
import type { BusinessPlan } from '../types/businessPlan';
import { logoColorToHex } from '../types/businessPlan';

export interface Slide {
  title: string;
  emoji: string;
  content: ReactNode;
}

export function buildSlides(plan: BusinessPlan): Slide[] {
  const slides: Slide[] = [];
  const s1 = plan.stageResponses?.ideaExplosion;
  const s2 = plan.stageResponses?.problemSolver;
  const s4 = plan.stageResponses?.mvpMadness;
  const s5 = plan.stageResponses?.moneyMoves;

  // ── 1. Cover ────────────────────────────────────────────────────────────────
  slides.push({
    title: 'Cover',
    emoji: '🚀',
    content: (
      <div
        className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden p-12 text-center"
        style={{ background: 'linear-gradient(145deg, #1e1b4b 0%, #0f172a 100%)' }}
      >
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(217,79,30,0.25) 0%, transparent 70%)' }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(217,79,30,0.12) 0%, transparent 70%)' }}
        />
        <div className="relative z-10 flex flex-col items-center gap-3">
          <span
            className="rounded-full px-5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white"
            style={{ backgroundColor: '#d94f1e' }}
          >
            Business Plan
          </span>
          <h1 className="mt-2 text-5xl font-extrabold leading-tight tracking-tight text-white">
            {plan.businessName || 'My Business'}
          </h1>
          <p className="text-lg font-medium" style={{ color: '#a5b4fc' }}>
            by {plan.explorerName}
          </p>
          <div className="my-1 h-px w-20" style={{ backgroundColor: '#3730a3' }} />
          {(plan.elevatorPitchSolution || s1?.s1_q1) && (
            <p className="max-w-md text-sm leading-relaxed" style={{ color: '#c7d2fe' }}>
              {plan.elevatorPitchSolution || s1?.s1_q1}
            </p>
          )}
        </div>
      </div>
    ),
  });

  // ── 2. The Problem ──────────────────────────────────────────────────────────
  slides.push({
    title: 'The Problem',
    emoji: '🔍',
    content: (
      <div className="flex h-full w-full flex-col overflow-hidden bg-white p-10">
        <div className="mb-5 flex items-center gap-3">
          <div className="h-1 w-8 rounded-full" style={{ backgroundColor: '#d94f1e' }} />
          <span
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: '#d94f1e' }}
          >
            The Problem
          </span>
        </div>

        <h2 className="mb-5 text-3xl font-extrabold leading-tight text-gray-900">
          {s2?.s2_q1 ? (
            <>&ldquo;{s2.s2_q1}&rdquo;</>
          ) : (
            'We identified a real problem worth solving'
          )}
        </h2>

        <div className="flex flex-1 gap-5 overflow-hidden">
          <div
            className="flex flex-1 flex-col justify-center rounded-2xl p-6"
            style={{ backgroundColor: '#fff8f5', border: '1.5px solid #fcd9cc' }}
          >
            <p
              className="mb-2 text-xs font-semibold uppercase tracking-wide"
              style={{ color: '#d94f1e' }}
            >
              Pain level
            </p>
            <p className="text-2xl font-bold text-gray-800">
              {s2?.s2_q2 || 'High'}
            </p>
            {s2?.s2_q1 && (
              <p className="mt-3 text-sm leading-relaxed text-gray-500">{s2.s2_q1}</p>
            )}
          </div>

          {plan.problemDiaryEntries && plan.problemDiaryEntries.length > 0 && (
            <div className="flex flex-1 flex-col gap-2 overflow-hidden">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Real evidence
              </p>
              {plan.problemDiaryEntries.slice(0, 3).map((entry, i) => (
                <div
                  key={i}
                  className="flex flex-1 flex-col justify-center overflow-hidden rounded-xl px-4 py-3"
                  style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
                >
                  <p className="truncate text-sm font-medium text-gray-700">
                    {entry.ughMoment}
                  </p>
                  {entry.location && (
                    <p className="mt-0.5 text-xs text-gray-400">📍 {entry.location}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    ),
  });

  // ── 3. Our Solution ─────────────────────────────────────────────────────────
  slides.push({
    title: 'Our Solution',
    emoji: '🌟',
    content: (
      <div
        className="flex h-full w-full flex-col overflow-hidden p-10"
        style={{ background: 'linear-gradient(145deg, #fff8f0 0%, #ffffff 60%)' }}
      >
        <div className="mb-5 flex items-center gap-3">
          <div className="h-1 w-8 rounded-full" style={{ backgroundColor: '#d94f1e' }} />
          <span
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: '#d94f1e' }}
          >
            Our Solution
          </span>
        </div>

        <div className="flex flex-1 items-center gap-8 overflow-hidden">
          <div className="min-w-0 flex-1">
            <h2 className="text-4xl font-extrabold leading-tight text-gray-900">
              {plan.businessName || 'Our Product'}
            </h2>
            {s1?.s1_q1 && (
              <p className="mt-3 text-xl font-semibold" style={{ color: '#d94f1e' }}>
                {s1.s1_q1}
              </p>
            )}
            {s1?.s1_q2 && (
              <p className="mt-3 text-sm leading-relaxed text-gray-600">{s1.s1_q2}</p>
            )}
          </div>

          <div className="flex w-40 shrink-0 flex-col gap-3">
            {s1?.s1_q3 && (
              <div
                className="rounded-2xl p-4 text-center"
                style={{ backgroundColor: '#fff0e8', border: '1.5px solid #fcd9cc' }}
              >
                <p className="text-4xl font-extrabold" style={{ color: '#d94f1e' }}>
                  {s1.s1_q3}<span className="text-2xl">/4</span>
                </p>
                <p className="mt-1 text-xs font-medium text-gray-500">friend hype</p>
              </div>
            )}
            <div
              className="rounded-2xl p-4 text-center"
              style={{ backgroundColor: '#f0fdf4', border: '1.5px solid #bbf7d0' }}
            >
              <p className="text-4xl font-extrabold text-green-700">6</p>
              <p className="mt-1 text-xs font-medium text-green-600">stage plan</p>
            </div>
          </div>
        </div>
      </div>
    ),
  });

  // ── 4. Our Customer ─────────────────────────────────────────────────────────
  slides.push({
    title: 'Our Customer',
    emoji: '🕵️',
    content: (
      <div
        className="flex h-full w-full flex-col overflow-hidden p-10"
        style={{ background: 'linear-gradient(145deg, #1e3a5f 0%, #0f2744 100%)' }}
      >
        <div className="mb-5 flex items-center gap-3">
          <div className="h-1 w-8 rounded-full bg-blue-400" />
          <span className="text-xs font-bold uppercase tracking-widest text-blue-300">
            Our Customer
          </span>
        </div>

        <div className="flex flex-1 gap-5 overflow-hidden">
          <div className="flex flex-1 flex-col gap-4 overflow-hidden">
            {(s4?.s4_q1 || s4?.s4_q2) && (
              <div
                className="rounded-2xl p-5"
                style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}
              >
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-300">
                  Who they are
                </p>
                {s4?.s4_q1 && (
                  <p className="font-medium text-white">{s4.s4_q1}</p>
                )}
                {s4?.s4_q2 && (
                  <p className="mt-1 text-sm text-blue-200">{s4.s4_q2}</p>
                )}
              </div>
            )}
            {plan.elevatorPitchWho && (
              <div
                className="rounded-2xl p-5"
                style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}
              >
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-300">
                  We serve
                </p>
                <p className="font-medium text-white">{plan.elevatorPitchWho}</p>
                {plan.elevatorPitchProblem && (
                  <p className="mt-1 text-sm text-blue-200">
                    who struggle with {plan.elevatorPitchProblem}
                  </p>
                )}
              </div>
            )}
          </div>

          {plan.ceoBigPromise && (
            <div
              className="flex flex-1 flex-col justify-center rounded-2xl p-6"
              style={{
                backgroundColor: 'rgba(217,79,30,0.15)',
                border: '1.5px solid rgba(217,79,30,0.3)',
              }}
            >
              <p
                className="mb-3 text-xs font-semibold uppercase tracking-wide"
                style={{ color: '#fb923c' }}
              >
                Our Promise
              </p>
              <p className="text-base font-medium leading-relaxed text-white">
                &ldquo;{plan.ceoBigPromise}&rdquo;
              </p>
              {plan.ceoCustomerFeeling && (
                <p className="mt-3 text-sm" style={{ color: '#fed7aa' }}>
                  Customers will feel: {plan.ceoCustomerFeeling}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    ),
  });

  // ── 5. Proof / Validation ───────────────────────────────────────────────────
  slides.push({
    title: 'Validation',
    emoji: '✅',
    content: (
      <div className="flex h-full w-full flex-col overflow-hidden bg-white p-10">
        <div className="mb-5 flex items-center gap-3">
          <div className="h-1 w-8 rounded-full bg-green-500" />
          <span className="text-xs font-bold uppercase tracking-widest text-green-600">
            People Want This
          </span>
        </div>

        {plan.friendSurveyResponses && plan.friendSurveyResponses.length > 0 ? (
          <div className="flex flex-1 gap-5 overflow-hidden">
            <div className="flex w-40 shrink-0 flex-col gap-3">
              <div className="rounded-2xl border border-green-100 bg-green-50 p-5 text-center">
                <p className="text-5xl font-extrabold text-green-700">
                  {plan.friendSurveyResponses.length}
                </p>
                <p className="mt-1 text-xs font-medium text-green-600">people surveyed</p>
              </div>
              <div
                className="rounded-2xl p-4 text-center"
                style={{ backgroundColor: '#fff8f5', border: '1.5px solid #fcd9cc' }}
              >
                <p className="text-3xl font-extrabold" style={{ color: '#d94f1e' }}>
                  100%
                </p>
                <p className="mt-1 text-xs font-medium text-gray-500">real feedback</p>
              </div>
            </div>

            <div className="flex flex-1 flex-col gap-2 overflow-hidden">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                What they said
              </p>
              {plan.friendSurveyResponses.slice(0, 4).map((r, i) => (
                <div
                  key={i}
                  className="flex flex-1 items-center gap-3 overflow-hidden rounded-xl px-4"
                  style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
                >
                  <div
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: '#d94f1e' }}
                  >
                    {i + 1}
                  </div>
                  <p className="truncate text-sm text-gray-700">{r.reaction}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <p className="mb-4 text-6xl">🤝</p>
            <h3 className="text-2xl font-bold text-gray-900">
              {plan.ceoCeoOath ? 'Our CEO Oath' : 'Customer Validation'}
            </h3>
            {plan.ceoCeoOath && (
              <p className="mt-4 max-w-md italic text-gray-600">
                &ldquo;{plan.ceoCeoOath}&rdquo;
              </p>
            )}
          </div>
        )}
      </div>
    ),
  });

  // ── 6. Business Model ───────────────────────────────────────────────────────
  slides.push({
    title: 'The Money',
    emoji: '💰',
    content: (
      <div
        className="flex h-full w-full flex-col overflow-hidden p-10"
        style={{ background: 'linear-gradient(145deg, #052e16 0%, #064e3b 100%)' }}
      >
        <div className="mb-5 flex items-center gap-3">
          <div className="h-1 w-8 rounded-full bg-green-400" />
          <span className="text-xs font-bold uppercase tracking-widest text-green-400">
            Business Model
          </span>
        </div>

        <div className="flex flex-1 gap-5 overflow-hidden">
          {plan.profitCalculation && (
            <div className="flex w-48 shrink-0 flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-green-400">
                Unit economics
              </p>
              {[
                {
                  label: 'Cost to make',
                  value: plan.profitCalculation.costToMake,
                  color: '#fca5a5',
                  bg: 'rgba(239,68,68,0.15)',
                },
                {
                  label: 'Selling price',
                  value: plan.profitCalculation.sellingPrice,
                  color: '#86efac',
                  bg: 'rgba(34,197,94,0.15)',
                },
                {
                  label: 'Profit per unit',
                  value:
                    plan.profitCalculation.sellingPrice -
                    plan.profitCalculation.costToMake,
                  color: '#4ade80',
                  bg: 'rgba(34,197,94,0.25)',
                },
              ].map((item) => (
                <div key={item.label} className="rounded-xl p-3" style={{ backgroundColor: item.bg }}>
                  <p className="text-xs font-medium text-green-300">{item.label}</p>
                  <p className="text-2xl font-extrabold" style={{ color: item.color }}>
                    ₹{item.value}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-1 flex-col gap-4 overflow-hidden">
            {s5?.s5_q1 && (
              <div
                className="rounded-2xl p-5"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
              >
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-green-400">
                  Price range
                </p>
                <p className="font-medium text-white">{s5.s5_q1}</p>
              </div>
            )}
            {s5?.s5_q4 && (
              <div
                className="rounded-2xl p-5"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
              >
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-green-400">
                  Strategy
                </p>
                <p className="font-medium text-white">{s5.s5_q4}</p>
              </div>
            )}
            {plan.wouldYouRatherItems && plan.wouldYouRatherItems.length > 0 && (
              <div
                className="rounded-2xl p-5"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
              >
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-green-400">
                  Products
                </p>
                <div className="flex flex-wrap gap-2">
                  {plan.wouldYouRatherItems.slice(0, 5).map((item, i) => (
                    <span
                      key={i}
                      className="rounded-full px-3 py-1 text-xs font-semibold text-white"
                      style={{
                        backgroundColor:
                          item.category === 'Must-Have'
                            ? 'rgba(217,79,30,0.8)'
                            : 'rgba(255,255,255,0.12)',
                      }}
                    >
                      {item.name}: {item.currency}{item.price}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {!s5?.s5_q1 && !s5?.s5_q4 && !plan.wouldYouRatherItems?.length && !plan.profitCalculation && (
              <div className="flex flex-1 items-center justify-center">
                <p className="text-center text-green-300">
                  Revenue model in development
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    ),
  });

  // ── 7. The Pitch ────────────────────────────────────────────────────────────
  slides.push({
    title: 'The Pitch',
    emoji: '📊',
    content: (
      <div
        className="flex h-full w-full flex-col items-center justify-center overflow-hidden p-12 text-center"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%), linear-gradient(145deg, #d94f1e 0%, #b83d16 100%)',
        }}
      >
        <div className="relative z-10 max-w-xl">
          <span className="mb-6 inline-block rounded-full bg-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
            The Elevator Pitch
          </span>
          <p className="text-2xl font-bold leading-relaxed text-white">
            {plan.elevatorPitchWho && (
              <>
                For{' '}
                <span style={{ color: '#fde68a' }}>{plan.elevatorPitchWho}</span>{' '}
              </>
            )}
            {plan.elevatorPitchProblem && (
              <>
                who struggle with{' '}
                <span style={{ color: '#fde68a' }}>{plan.elevatorPitchProblem}</span>,{' '}
              </>
            )}
            {plan.elevatorPitchSolution && (
              <span className="font-extrabold">{plan.elevatorPitchSolution} </span>
            )}
            {plan.elevatorPitchWhy && (
              <>
                because{' '}
                <span style={{ color: '#fde68a' }}>{plan.elevatorPitchWhy}</span>.
              </>
            )}
            {!plan.elevatorPitchWho &&
              !plan.elevatorPitchProblem &&
              !plan.elevatorPitchSolution && (
                <span>
                  {plan.businessName || 'Our business'} is here to make a difference.
                </span>
              )}
          </p>
        </div>
      </div>
    ),
  });

  // ── 8. Brand Identity (optional) ────────────────────────────────────────────
  const hasLogo = plan.selectedLogoColors?.length || plan.selectedLogoShape;
  if (hasLogo) {
    const bgHex = plan.selectedLogoColors?.[0]
      ? logoColorToHex(plan.selectedLogoColors[0])
      : '#6366f1';
    const accentHex = plan.selectedLogoColors?.[1]
      ? logoColorToHex(plan.selectedLogoColors[1])
      : undefined;
    const shape =
      plan.selectedLogoShape === 'circle'
        ? '●'
        : plan.selectedLogoShape === 'star'
        ? '★'
        : plan.selectedLogoShape === 'diamond'
        ? '◆'
        : plan.selectedLogoShape === 'heart'
        ? '♥'
        : '■';

    slides.push({
      title: 'Brand',
      emoji: '🎨',
      content: (
        <div className="flex h-full w-full overflow-hidden bg-white">
          <div className="flex flex-1 flex-col justify-center p-10">
            <div className="mb-5 h-1 w-8 rounded-full" style={{ backgroundColor: bgHex }} />
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
              Brand Identity
            </p>
            <h2 className="mt-3 text-3xl font-extrabold text-gray-900">
              {plan.businessName || 'Our Brand'}
            </h2>
            {plan.logoKeywords && (
              <p className="mt-3 text-sm text-gray-500">
                <span className="font-semibold text-gray-700">Keywords: </span>
                {plan.logoKeywords}
              </p>
            )}
            {plan.selectedLogoColors && plan.selectedLogoColors.length > 0 && (
              <div className="mt-5 flex gap-3">
                {plan.selectedLogoColors.map((c, i) => (
                  <div
                    key={i}
                    className="h-10 w-10 rounded-full shadow-md"
                    style={{ backgroundColor: logoColorToHex(c) }}
                  />
                ))}
              </div>
            )}
          </div>

          <div
            className="flex flex-1 items-center justify-center"
            style={{ backgroundColor: `${bgHex}18` }}
          >
            <div
              className="flex items-center justify-center rounded-3xl font-bold text-white shadow-2xl"
              style={{
                width: 150,
                height: 150,
                backgroundColor: bgHex,
                fontSize: 60,
                border: accentHex ? `8px solid ${accentHex}` : undefined,
              }}
            >
              {shape}
            </div>
          </div>
        </div>
      ),
    });
  }

  // ── 9. Thank You ────────────────────────────────────────────────────────────
  slides.push({
    title: 'Thank You',
    emoji: '🎯',
    content: (
      <div
        className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden p-12 text-center"
        style={{ background: 'linear-gradient(145deg, #1e1b4b 0%, #0f172a 100%)' }}
      >
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(217,79,30,0.22) 0%, transparent 70%)' }}
        />
        <div className="relative z-10 flex flex-col items-center gap-3">
          <p className="text-6xl">🎯</p>
          <h2 className="mt-2 text-4xl font-extrabold text-white">Thank You</h2>
          <p className="text-xl font-semibold" style={{ color: '#fb923c' }}>
            {plan.businessName || 'My Business'}
          </p>
          <p style={{ color: '#a5b4fc' }}>by {plan.explorerName}</p>
          {plan.ceoCeoOath && (
            <p className="mt-4 max-w-sm text-sm italic" style={{ color: '#c7d2fe' }}>
              &ldquo;{plan.ceoCeoOath}&rdquo;
            </p>
          )}
        </div>
      </div>
    ),
  });

  return slides;
}
